import { isqrt } from "./dashboard";
import { daysBetween } from "./finance";

/**
 * Planejador de compras (T3.7, 06-ia-agentes §3): previsão de demanda por SKU e quantidade sugerida, só com inteiros
 * (q4; fatores em milésimos; BigInt nas contas que podem passar de 2^53). Método: Holt amortecido (ETS AAdN) sobre a
 * demanda SEMANAL dos últimos 180 dias — a semana absorve a sazonalidade do dia da semana —, estoque de segurança
 * `z × σ × √(L/7)` (σ = raiz do erro quadrático médio das previsões de um passo), ponto de pedido e alvo. Sem venda no
 * histórico vale a regra mínimo/máximo do cadastro. O modelo de linguagem nunca mexe nestes números.
 */

export const PLANNER_DEFAULTS = {
  /** Suavização do nível (α), da tendência (β) e amortecimento (φ), em milésimos. */
  alpha: 300,
  beta: 100,
  phi: 900,
  /** z do nível de serviço × 100 (95% = 1,65). */
  serviceZ100: 165,
  historyDays: 180,
  leadTimeDays: 7,
  coverDays: 30,
} as const;

const U = 10_000n;
const big = (n: number) => BigInt(n);

/** Divisão com arredondamento comercial (meio para longe do zero); `den` > 0. */
function rdiv(num: bigint, den: bigint): bigint {
  const neg = num < 0n;
  const a = neg ? -num : num;
  const q = (2n * a + den) / (2n * den);
  return neg ? -q : q;
}
const ceilDiv = (num: bigint, den: bigint) => (num <= 0n ? 0n : (num + den - 1n) / den);
const toNum = (v: bigint) => {
  const n = Number(v);
  return Number.isSafeInteger(n) ? n : Number.MAX_SAFE_INTEGER;
};
const max = (a: bigint, b: bigint) => (a > b ? a : b);
const min = (a: bigint, b: bigint) => (a < b ? a : b);

/** Semanas completas (7 dias) contadas do fim da série; da mais antiga para a mais recente. */
export function weeklyBuckets(dailyQ4: readonly number[]): number[] {
  const n = Math.floor(dailyQ4.length / 7);
  const start = dailyQ4.length - n * 7;
  const out: number[] = [];
  for (let w = 0; w < n; w++) {
    let s = 0n;
    for (let d = 0; d < 7; d++) s += big(Math.max(0, dailyQ4[start + w * 7 + d] ?? 0));
    out.push(toNum(s));
  }
  return out;
}

type Holt = { level: bigint; trend: bigint; sigma: bigint };

/** Holt amortecido na forma de correção de erro (inteiros): l = l + φb + αe; b = φb + αβe. */
function holt(weeks: readonly number[]): Holt {
  const { alpha, beta, phi } = PLANNER_DEFAULTS;
  const A = big(alpha);
  const AB = big(alpha * beta);
  const P = big(phi);
  const warm = Math.min(4, weeks.length);
  let level = rdiv(
    weeks.slice(0, warm).reduce((s, w) => s + big(w), 0n),
    big(Math.max(1, warm)),
  );
  let trend = 0n;
  let sq = 0n;
  let n = 0n;
  for (let t = warm; t < weeks.length; t++) {
    const f = level + rdiv(P * trend, 1000n);
    const e = big(weeks[t]!) - f;
    sq += e * e;
    n += 1n;
    level = f + rdiv(A * e, 1000n);
    trend = rdiv(P * trend, 1000n) + rdiv(AB * e, 1_000_000n);
  }
  return { level, trend, sigma: n > 0n ? isqrt(rdiv(sq, n)) : 0n };
}

/** Demanda prevista para os próximos `days` dias (semana k = l + (φ + … + φ^k)·b, nunca negativa). */
function demandOver(h: Holt, days: number): bigint {
  const P = big(PLANNER_DEFAULTS.phi);
  let total = 0n;
  let pk = 1000n;
  let cum = 0n;
  for (let k = 1; (k - 1) * 7 < days; k++) {
    pk = rdiv(pk * P, 1000n);
    cum += pk;
    const week = max(0n, h.level + rdiv(cum * h.trend, 1000n));
    const share = big(Math.min(7, days - (k - 1) * 7));
    total += rdiv(week * share, 7n);
  }
  return total;
}

export type ReorderInput = {
  /** Vendas por dia em q4, da mais antiga para ontem (até 180). */
  dailyQ4: readonly number[];
  /** Disponível (físico − reservado) somado nos depósitos. */
  availableQ4: number;
  /** A receber: saldo dos pedidos de compra abertos. */
  onOrderQ4: number;
  stockMinQ4: number | null;
  stockMaxQ4: number | null;
  leadTimeDays: number;
  /** Período de revisão (cobertura desejada depois que o pedido chega). */
  coverDays: number;
  /** Itens por caixa (múltiplo de compra, em unidades). */
  itemsPerBox: number | null;
  serviceZ100?: number;
};

export type ReorderPlan = {
  method: "holt" | "min_max";
  weeklyForecastQ4: number;
  dailyForecastQ4: number;
  demandLeadQ4: number;
  safetyQ4: number;
  reorderPointQ4: number;
  positionQ4: number;
  targetQ4: number;
  /** Quantidade a comprar (q4, unidade inteira e múltiplo da caixa); 0 = nada. */
  suggestedQ4: number;
  /** Dias que a posição atual cobre (null = sem demanda). */
  coverageDays: number | null;
  trend: "up" | "down" | "flat";
  soldQ4: number;
};

/** Arredonda para unidade inteira e múltiplo da caixa (nunca negativo). */
function roundPurchase(q: bigint, itemsPerBox: number | null): bigint {
  const units = ceilDiv(q, U);
  const box = big(itemsPerBox && itemsPerBox > 1 ? itemsPerBox : 1);
  return ceilDiv(units, box) * box * U;
}

export function planReorder(i: ReorderInput): ReorderPlan {
  const position = big(i.availableQ4) + big(Math.max(0, i.onOrderQ4));
  const minQ = i.stockMinQ4 != null && i.stockMinQ4 > 0 ? big(i.stockMinQ4) : null;
  const maxQ = i.stockMaxQ4 != null && i.stockMaxQ4 > 0 ? big(i.stockMaxQ4) : null;
  const sold = i.dailyQ4.reduce((s, v) => s + big(Math.max(0, v)), 0n);
  const weeks = weeklyBuckets(i.dailyQ4);
  if (sold === 0n || weeks.length === 0) {
    const target = minQ !== null && position <= minQ ? (maxQ ?? minQ) : 0n;
    const suggested = target > position ? roundPurchase(target - position, i.itemsPerBox) : 0n;
    return {
      method: "min_max",
      weeklyForecastQ4: 0,
      dailyForecastQ4: 0,
      demandLeadQ4: 0,
      safetyQ4: 0,
      reorderPointQ4: toNum(minQ ?? 0n),
      positionQ4: toNum(position),
      targetQ4: toNum(target),
      suggestedQ4: toNum(suggested),
      coverageDays: null,
      trend: "flat",
      soldQ4: toNum(sold),
    };
  }
  const h = holt(weeks);
  const lead = Math.max(1, Math.min(365, Math.floor(i.leadTimeDays)));
  const cover = Math.max(0, Math.min(365, Math.floor(i.coverDays)));
  const weekly = demandOver(h, 7);
  const z = big(i.serviceZ100 ?? PLANNER_DEFAULTS.serviceZ100);
  // σ semanal × √(L/7): √(L/7) em milésimos = isqrt(L × 10^6 / 7).
  const root = isqrt(rdiv(big(lead) * 1_000_000n, 7n));
  const safety = rdiv(z * h.sigma * root, 100_000n);
  const demandLead = demandOver(h, lead);
  let rop = demandLead + safety;
  if (minQ !== null && rop < minQ) rop = minQ;
  let target = demandOver(h, lead + cover) + safety;
  if (minQ !== null) target = max(target, minQ);
  if (maxQ !== null) target = min(target, maxQ);
  const suggested =
    position <= rop && target > position ? roundPurchase(target - position, i.itemsPerBox) : 0n;
  // Tendência: ±1% do nível por semana (o amortecimento estima cerca de metade da inclinação real).
  const trend =
    h.level > 0n && h.trend * 100n > h.level
      ? "up"
      : h.level > 0n && -h.trend * 100n > h.level
        ? "down"
        : "flat";
  return {
    method: "holt",
    weeklyForecastQ4: toNum(weekly),
    dailyForecastQ4: toNum(rdiv(weekly, 7n)),
    demandLeadQ4: toNum(demandLead),
    safetyQ4: toNum(safety),
    reorderPointQ4: toNum(rop),
    positionQ4: toNum(position),
    targetQ4: toNum(target),
    suggestedQ4: toNum(suggested),
    coverageDays: weekly > 0n ? toNum(position <= 0n ? 0n : (position * 7n) / weekly) : null,
    trend,
    soldQ4: toNum(sold),
  };
}

/** Lead time do fornecedor: média dos últimos 10 recebimentos (data do pedido → recebimento), entre 1 e 120 dias. */
export function learnLeadTimeDays(
  receipts: readonly { orderDate: string; receivedDate: string }[],
  fallback: number,
): number {
  const last = receipts.slice(-10);
  if (!last.length) return fallback;
  const total = last.reduce((s, r) => s + Math.max(0, daysBetween(r.orderDate, r.receivedDate)), 0);
  return Math.max(1, Math.min(120, Math.round(total / last.length)));
}
